import styled from "@emotion/styled";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Close } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Icon,
  Input,
  Link,
  RadiusToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { isEmail } from "helpers/validate";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useChatWidget } from "hooks/use-chat-widget";
import { InstructionsModalViewActions } from "store/views/forwarding-instructions/actions";
import { selectIsForwardingInstructionsSending } from "store/views/forwarding-instructions/selectors";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { Location } from "./ProviderAccordions";

interface IProps extends IWithBreakpointsProps {
  location: Location;
  isMobile: boolean;
  opened: boolean;
  onOpen: () => void;
  onClose: () => void;
  trackWithLocation: (
    eventName: string,
    location: Location,
    data?: Record<string, unknown>,
    skipWelcome?: boolean
  ) => void;
}

const SendInstructionsComponent: FC<IProps> = ({ location, isMobile, opened, onClose, onOpen, trackWithLocation }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { openChatWidget } = useChatWidget();

  const isSendingInstructions = useSelector(selectIsForwardingInstructionsSending);

  const disableSubmit = isSendingInstructions || !email;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSubmit = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isEmail(email)) {
      setEmailError(true);
      return;
    }
    dispatch(InstructionsModalViewActions.sendForwardingInstructions(email));
    trackWithLocation("Instructions sent to email", location, { email });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    emailError && setEmailError(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleOpenChatWidget = () => {
    trackWithLocation("'Chat with us' clicked", location);
    openChatWidget();
    onClose();
  };

  useEffect(() => {
    if (opened) {
      inputRef.current?.focus();
    }
  }, [opened]);

  if (location === Location.LiveChat) {
    return null;
  }

  return (
    <Block>
      <Text textAlign="left" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        Need help?{" "}
        <Link bold={false} onClick={onOpen} data-testid="send-instructions-link">
          Send instructions
        </Link>{" "}
        to your email administrator
        {isMobile ? (
          "."
        ) : (
          <>
            {" "}
            or{" "}
            <Link bold={false} onClick={handleOpenChatWidget}>
              chat with us
            </Link>
            .
          </>
        )}
      </Text>
      <SendInstructionsContainer formOpened={opened}>
        <Form onSubmit={handleSubmit}>
          <InputContainer labelText="Send the instruction to" error={emailError ? "Incorrect email" : undefined}>
            <Input
              id="send-instructions"
              placeholder="Type email address"
              onChange={handleInputChange}
              ref={inputRef}
              data-testid="form-input"
              data-1p-ignore
            />
          </InputContainer>
          <SubmitButton
            type="submit"
            kind="secondary"
            loading={isSendingInstructions}
            disabled={disableSubmit}
            data-testid="form-send-button"
          >
            Send
          </SubmitButton>
          <CloseButton size="xcompact" kind="text" icon={<Icon source={Close} />} onClick={onClose} />
        </Form>
      </SendInstructionsContainer>
    </Block>
  );
};

const SendInstructionsContainer = styled.div<{ formOpened: boolean }>`
  overflow: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ formOpened }: any) => (formOpened ? "visible" : "hidden")
  };
  opacity: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ formOpened }: any) => (formOpened ? 1 : 0)
  };
  max-height: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ formOpened }: any) => (formOpened ? "105px" : "0px")
  };
`;

const InputContainer = styled(FormField)`
  margin-bottom: 0;
  margin-right: ${withVar(SpacingToken.Spacing2)};
  width: 100%;
`;

const SubmitButton = styled(Button)`
  margin-top: ${withVar(SpacingToken.Spacing6)};
`;

const CloseButton = styled(Button)`
  margin-left: ${withVar(SpacingToken.Spacing3)};
`;

const Form = styled.form`
  display: flex;
  align-items: flex-start;
  padding: ${withVar(SpacingToken.Spacing3)};
  margin-top: ${withVar(SpacingToken.Spacing3)};
  background: ${withVar(DesignToken.SurfaceAccentEmphasisMinInfo)};
  border-radius: ${withVar(RadiusToken.Radius3)};
`;

export const SendInstructions = withBreakpoints(SendInstructionsComponent);
